import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { inboxActions } from "_actions";
import { inboxService } from "_services";
import DMHeaderDetails from "./DMHeaderDetails";
import GeneratedResponse from "./GeneratedResponse";
import ReplyBox from "./ReplyBox";
import MessageThread from "./MessageThread";

const MESSAGE_LOCK_REFRESH_INTERVAL_MS = 10000;

export default function DirectMessageDetailsContainer({
  conversation,
  conversationList,
  sourceName,
  currentUser,
  link,
  author,
  companyReplied,
  markAsDone,
  openedMessageId,
}) {
  const dispatch = useDispatch();
  const [replyText, setReplyText] = useState("");
  const heldByAnotherAdmin = useSelector(
    (state) => state.inbox.messageLock?.heldByAnotherAdmin,
  );

  // Hold the lock while this admin has the message open, and release it when they leave
  // so another admin is not left thinking someone is still viewing. pagehide covers closing the tab.
  useEffect(() => {
    if (!openedMessageId) {
      return undefined;
    }

    dispatch(inboxActions.lockMessage(openedMessageId));

    const releaseLock = () => {
      inboxService.unlockMessage(openedMessageId);
    };
    window.addEventListener("pagehide", releaseLock);

    return () => {
      window.removeEventListener("pagehide", releaseLock);
      releaseLock();
    };
  }, [dispatch, openedMessageId]);

  // While someone else holds the lock, check again so this page drops the badge once they leave.
  useEffect(() => {
    if (!openedMessageId || !heldByAnotherAdmin) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      dispatch(
        inboxActions.lockMessage(openedMessageId, { keepExisting: true }),
      );
    }, MESSAGE_LOCK_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [dispatch, openedMessageId, heldByAnotherAdmin]);

  return (
    <Box sx={styles.wrapper}>
      <DMHeaderDetails sourceName={sourceName} author={author} />
      <MessageThread
        conversation={conversationList}
        currentUser={currentUser}
        sourceName={sourceName}
      />
      <Box sx={{ padding: "15px", borderTop: "1px solid rgb(241, 237, 230)" }}>
        <GeneratedResponse
          draft={conversation?.draft}
          messageId={openedMessageId}
          onUseSuggested={setReplyText}
        />
        <ReplyBox
          conversation={conversation}
          currentUser={currentUser}
          companyReplied={companyReplied}
          markAsDone={markAsDone}
          openedMessageId={openedMessageId}
          link={link}
          replyText={replyText}
          onReplyTextChange={setReplyText}
        />
      </Box>
    </Box>
  );
}

const styles = {
  wrapper: {
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    overflow: "hidden",
    height: "calc(100vh - 150px)",
    boxShadow: "rgba(18, 38, 27, 0.05) 0px 1px 3px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    maxWidth: "1100px",
  },
};
